import isEmpty from 'lodash/isEmpty';
import Quill from 'quill';
import {
  forwardRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
} from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { useEmbedLy } from '@hivebrite/react-hooks';

import Embed from 'hb-react/shared/components/DesignSystem/Embed';
import { MAX_HEIGHT } from 'hb-react/shared/components/DesignSystem/EmbedLy/constants/heights';
import { standardUrlRegex } from 'hb-react/shared/utils/validators/urlFormat';

import { useQuillEditorContext } from '../contexts/QuillEditorContext';

const filterUrls = (urls) =>
  urls.filter((url) => /^[^.]+$|\.(?!(gif|jpg|jpeg|png)$)([^.]+$)/.test(url));
const types = ['link', 'video'];

const EmbedLy = forwardRef((_, ref) => {
  const {
    initialEmbed,
    offEditorChange,
    onAddEmbed,
    onEditorChange,
    onRemoveEmbed,
    quillRef,
  } = useQuillEditorContext();

  const [plainText, setPlainText] = useState(initialEmbed?.url || null);
  const [currentEmbed, setCurrentEmbed] = useState(
    isEmpty(initialEmbed) ? null : initialEmbed,
  );

  const { response, isLoading } = useEmbedLy(plainText, {
    apiUrl: `${window.location.origin}${Routes.embed_path()}`,
    // Max chars in description
    chars: 150,
    // Omit images
    filterUrls,
    maxHeight: MAX_HEIGHT,
    types,
    autoplay: true,
    regex: standardUrlRegex,
  });

  const handleEditorChange = useCallback(
    (eventName) => {
      if (eventName === Quill.events.TEXT_CHANGE) {
        const text = quillRef.current.getText();

        setPlainText(text.trim().split('\n').join(' '));
      }
    },
    [quillRef],
  );
  const handleRemoveEmbed = useCallback(() => {
    setPlainText(null);
    setCurrentEmbed(null);

    quillRef.current.focus();

    if (onRemoveEmbed) {
      onRemoveEmbed();
    }
  }, [onRemoveEmbed, quillRef]);

  useEffect(() => {
    if (response && !isLoading) {
      setCurrentEmbed(response);
    }
  }, [response, isLoading]);

  useEffect(() => {
    if (currentEmbed && onAddEmbed) {
      onAddEmbed(currentEmbed);
    }
  }, [onAddEmbed, currentEmbed]);

  // Set event for QuillJS
  useEffect(() => {
    onEditorChange(handleEditorChange);

    return () => offEditorChange(handleEditorChange);
  }, [onEditorChange, offEditorChange, handleEditorChange]);

  useImperativeHandle(
    ref,
    () => ({
      setEmbed: (embed) => setCurrentEmbed(embed),
      setPlainText: (url) => setPlainText(url),
      clearEmbed: () => {
        if (quillRef.current) {
          setPlainText(null);
          setCurrentEmbed(null);
        }
      },
      hasContent: () => !isEmpty(currentEmbed),
    }),
    [currentEmbed, quillRef],
  );

  return (
    (!isEmpty(currentEmbed) || isLoading) && (
      <Box mx={3} mt={3} mb={6} minHeight="min-content">
        <Embed
          embed={currentEmbed}
          isLoading={!currentEmbed && isLoading}
          onClose={handleRemoveEmbed}
        />
      </Box>
    )
  );
});

export default EmbedLy;
